import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  api,
  invalidateAuditDocumentQueries,
  type CompleteUploadDocumentDto,
  type InitUploadDocumentItemDto,
  type InitUploadDocumentResponseItemDto,
  type InitUploadDocumentsDto,
  type InitUploadDocumentsResponseDto,
  type UploadDocumentDto,
  extractErrorMessage,
} from '@sib/api';
import { QueryKey } from '@sib/api/enums';
import { useUserSetupStore } from '@sib/auth';
import { DOCUMENTS_DROPZONE_MAX_SIZE_BYTES } from '@sib/config';
import { useTranslation } from '@sib/i18n';
import { useNotification } from '@sib/notifications';
import { FileItem } from '@sib/types';
import { CHUNKED_UPLOAD_SESSION_TTL_MS } from '@sib/utils';
import { v4 as uuidv4 } from 'uuid';
import {
  isInvalidBlobContentError,
  uploadFileInChunks,
} from './chunked-upload';
import {
  buildChunkedUploadSessionKey,
  clearPersistedChunkedUploadSession,
  loadPersistedChunkedUploadSession,
  markPersistedChunkedUploadBlockComplete,
  pruneStaleUploadSessions,
  resetPersistedChunkedUploadBlockProgress,
  savePersistedChunkedUploadSession,
} from './chunked-upload-session';
import {
  DocumentUploadFileWithMd5,
  validateDocumentUploadByMd5,
} from './document-upload-md5';
import type { FileUploadWithCallbacksProps, UploadFileCallback } from './types';
import { isAbortError } from './utils';

export type AuditUploadContext = {
  auditId: string;
  organizationId: string;
};

interface UploadToBlobStorageProps extends FileUploadWithCallbacksProps {
  organizationId?: string;
  uploadFileCallback?: UploadFileCallback;
  auditUpload?: AuditUploadContext;
  documentUploadOptions?: DocumentUploadOptions;
}

type UploadDocumentFileOptions = {
  batchUploadId?: string;
  totalFiles: number;
  md5Hash: string;
  abortSignal?: AbortSignal;
  initSession?: InitUploadDocumentResponseItemDto;
};

type InitDocumentUploadSessionsOptions = {
  batchUploadId?: string;
  totalFiles: number;
  files: DocumentUploadFileWithMd5[];
  abortSignal?: AbortSignal;
  auditUpload?: AuditUploadContext;
  documentUploadOptions?: DocumentUploadOptions;
};

type ValidateDocumentUploadOptions = {
  fileItems: FileItem[];
  resolveFileItem?: (fileId: string) => FileItem | undefined;
};

export type DocumentUploadOptions = {
  defaultInternal?: boolean;
  includeInternalField?: boolean;
};

const buildInitUploadFilePayload = (
  fileItem: FileItem,
  md5Hash: string,
  documentUploadOptions?: DocumentUploadOptions
): InitUploadDocumentItemDto => {
  const payload: InitUploadDocumentItemDto = {
    filename: fileItem.file.name,
    mimetype: fileItem.file.type || 'application/octet-stream',
    sensitive: fileItem.isSensitive ?? false,
    md5Hash,
    filesize: fileItem.file.size,
  };

  if (documentUploadOptions?.includeInternalField !== false)
    payload.internal =
      fileItem.isInternal ?? documentUploadOptions?.defaultInternal ?? false;

  return payload;
};

const requestInitUploadSessions = (
  organizationId: string,
  payload: InitUploadDocumentsDto,
  auditUpload: AuditUploadContext | undefined,
  abortSignal?: AbortSignal
): Promise<InitUploadDocumentsResponseDto> =>
  auditUpload
    ? api.internalPortal.auditDocumentControllerInitUploadSession(
        auditUpload.auditId,
        auditUpload.organizationId,
        payload,
        { signal: abortSignal }
      )
    : api.organization.documentControllerInitUploadSession(
        organizationId,
        payload,
        {
          signal: abortSignal,
        }
      );

const requestCompleteUploadSession = (
  organizationId: string,
  payload: CompleteUploadDocumentDto,
  auditUpload: AuditUploadContext | undefined,
  abortSignal?: AbortSignal
) =>
  auditUpload
    ? api.internalPortal.auditDocumentControllerCompleteUploadSession(
        auditUpload.auditId,
        auditUpload.organizationId,
        payload,
        { signal: abortSignal }
      )
    : api.organization.documentControllerCompleteUploadSession(
        organizationId,
        payload,
        { signal: abortSignal }
      );

export const initDocumentUploadSessions = async (
  organizationId: string,
  {
    batchUploadId,
    totalFiles,
    files,
    abortSignal,
    auditUpload,
    documentUploadOptions,
  }: InitDocumentUploadSessionsOptions
) => {
  const payload: InitUploadDocumentsDto = {
    batchUploadId,
    totalFiles,
    files: files.map(({ fileItem, md5Hash }) =>
      buildInitUploadFilePayload(fileItem, md5Hash, documentUploadOptions)
    ),
  };

  const response = await requestInitUploadSessions(
    organizationId,
    payload,
    auditUpload,
    abortSignal
  );

  const sessionsByFilename = new Map(
    response.items.map(session => [session.filename, session])
  );

  return {
    sessionsByFilename,
    failed: response.failed,
  };
};

export const useUploadToBlobStorage = ({
  onProgress,
  onError,
  organizationId,
  onSuccess,
  uploadFileCallback,
  auditUpload,
  documentUploadOptions,
}: UploadToBlobStorageProps) => {
  const { t } = useTranslation();
  const { open } = useNotification();
  const queryClient = useQueryClient();
  const {
    organization: { id: defaultOrganizationId },
  } = useUserSetupStore();
  const effectiveOrganizationId = organizationId ?? defaultOrganizationId;

  useEffect(() => pruneStaleUploadSessions(CHUNKED_UPLOAD_SESSION_TTL_MS), []);

  const invalidateQueriesAfterUpload = useCallback(() => {
    if (auditUpload) {
      invalidateAuditDocumentQueries(
        queryClient,
        auditUpload.auditId,
        auditUpload.organizationId
      );

      return;
    }

    queryClient.invalidateQueries({
      queryKey: [QueryKey.Resource.Documents],
    });
  }, [auditUpload, queryClient]);

  const validateDocumentUpload = useCallback(
    async ({
      fileItems,
      resolveFileItem,
    }: ValidateDocumentUploadOptions): Promise<DocumentUploadFileWithMd5[]> => {
      try {
        const duplicateErrors: string[] = [];

        const validatedFiles = await validateDocumentUploadByMd5({
          organizationId: effectiveOrganizationId || '',
          fileItems,
          resolveFileItem,
          onDuplicate: (fileItem, error) => {
            duplicateErrors.push(error);
            onError?.(fileItem, error);
          },
        });

        if (duplicateErrors.length > 0) {
          const errorDescription = [
            ...new Set(duplicateErrors.filter(Boolean)),
          ].join('\n');

          if (errorDescription) {
            open?.({
              type: 'error',
              message: t('fileManager.uploadFailedMessage'),
              description: errorDescription,
            });
          }
        }

        return validatedFiles;
      } catch (error) {
        const message = extractErrorMessage(
          error,
          t('fileManager.uploadFailedMessage'),
          { defaultMessageStatusCodes: [500, 404] }
        );

        fileItems.forEach(fileItem => onError?.(fileItem, message, error));

        return [];
      }
    },
    [effectiveOrganizationId, onError, open, t]
  );

  const uploadDocumentFile = useCallback(
    async (fileItem: FileItem, options: UploadDocumentFileOptions) => {
      const { batchUploadId, totalFiles, md5Hash, abortSignal, initSession } =
        options;

      onProgress?.(fileItem, 0);

      const sessionKey = buildChunkedUploadSessionKey({
        organizationId: effectiveOrganizationId || '',
        filename: fileItem.file.name,
        filesize: fileItem.file.size,
        md5Hash,
      });

      const initUploadSession = async (restart: boolean) => {
        if (!restart && initSession) return initSession;

        const response = await requestInitUploadSessions(
          effectiveOrganizationId || '',
          {
            batchUploadId,
            totalFiles,
            files: [
              {
                ...buildInitUploadFilePayload(
                  fileItem,
                  md5Hash,
                  documentUploadOptions
                ),
                restart: restart || undefined,
              },
            ],
          },
          auditUpload,
          abortSignal
        );

        const failedItem = response.failed.find(
          item => item.filename === fileItem.file.name
        );

        if (failedItem) throw new Error(failedItem.error);

        const session = response.items.find(
          item => item.filename === fileItem.file.name
        );

        if (!session) throw new Error(t('fileManager.uploadFailedMessage'));

        return session;
      };

      const runUploadAttempt = async (restart: boolean) => {
        const session = await initUploadSession(restart);

        const completedBlockIndices = new Set<number>();
        const canResumePartialBlocks =
          !restart &&
          session.resumePartialBlocks &&
          loadPersistedChunkedUploadSession(sessionKey)?.uploadId ===
            session.uploadId;

        const persistedSession = loadPersistedChunkedUploadSession(sessionKey);
        if (canResumePartialBlocks) {
          persistedSession?.completedBlockIndices.forEach(blockIndex => {
            completedBlockIndices.add(blockIndex);
          });
        } else if (restart) {
          clearPersistedChunkedUploadSession(sessionKey);
          onProgress?.(fileItem, 0);
        } else if (persistedSession?.uploadId === session.uploadId)
          resetPersistedChunkedUploadBlockProgress(sessionKey);

        savePersistedChunkedUploadSession(sessionKey, {
          organizationId: effectiveOrganizationId || '',
          filename: fileItem.file.name,
          filesize: fileItem.file.size,
          md5Hash,
          uploadId: session.uploadId,
          blockSize: session.blockSize,
          completedBlockIndices: [...completedBlockIndices],
          createdAt: Date.now(),
        });

        const { blockIds } = await uploadFileInChunks({
          file: fileItem.file,
          sasUrl: session.sasUrl,
          blockSize: session.blockSize,
          md5HashBase64: md5Hash,
          abortSignal,
          completedBlockIndices,
          onBlockComplete: blockIndex => {
            markPersistedChunkedUploadBlockComplete(sessionKey, blockIndex);
          },
          onProgress: (loaded, total) =>
            onProgress?.(fileItem, Math.round((loaded * 100) / total)),
        });

        await requestCompleteUploadSession(
          effectiveOrganizationId || '',
          {
            uploadId: session.uploadId,
            blockIds,
          },
          auditUpload,
          abortSignal
        );

        clearPersistedChunkedUploadSession(sessionKey);
      };

      try {
        if (fileItem.file.size > DOCUMENTS_DROPZONE_MAX_SIZE_BYTES)
          return onError?.(
            fileItem,
            t('fileManager.maxFileSize', {
              size: `${DOCUMENTS_DROPZONE_MAX_SIZE_BYTES / (1024 * 1024 * 1024)} GB`,
            })
          );

        if (uploadFileCallback) {
          const uploadPayload: UploadDocumentDto = {
            file: fileItem.file,
            filesize: fileItem.file.size,
            totalFiles,
            batchUploadId,
            sensitive: fileItem.isSensitive ?? false,
            md5Hash,
          };

          await uploadFileCallback(uploadPayload, { signal: abortSignal });

          invalidateQueriesAfterUpload();

          onProgress?.(fileItem, 100);
          onSuccess?.(fileItem);

          return;
        }

        try {
          await runUploadAttempt(false);
        } catch (error) {
          if (isAbortError(error)) {
            clearPersistedChunkedUploadSession(sessionKey);

            return onError?.(
              fileItem,
              t('fileManager.uploadCancelledMessage'),
              error
            );
          }

          if (isInvalidBlobContentError(error)) {
            await runUploadAttempt(true);
          } else {
            clearPersistedChunkedUploadSession(sessionKey);
            throw error;
          }
        }

        invalidateQueriesAfterUpload();

        onProgress?.(fileItem, 100);
        onSuccess?.(fileItem);
      } catch (error) {
        if (isAbortError(error)) {
          clearPersistedChunkedUploadSession(sessionKey);

          return onError?.(
            fileItem,
            t('fileManager.uploadCancelledMessage'),
            error
          );
        }

        clearPersistedChunkedUploadSession(sessionKey);
        onError?.(
          fileItem,
          extractErrorMessage(error, t('fileManager.uploadFailedMessage')),
          error
        );
      }
    },
    [
      auditUpload,
      effectiveOrganizationId,
      invalidateQueriesAfterUpload,
      onError,
      onProgress,
      onSuccess,
      t,
      uploadFileCallback,
      documentUploadOptions,
    ]
  );

  return {
    validateDocumentUpload,
    uploadDocumentFile,
    initDocumentUploadSessions,
  };
};

export const useBatchUpload = () => {
  const generateBatchId = useCallback(() => uuidv4(), []);

  return {
    generateBatchId,
  };
};
